import { Icon } from "@mdi/react";
import { useState } from "react";

import { mdiFormatColorFill, mdiFormatPaint, mdiPalette } from "@mdi/js";
import { Button, Popover, Tooltip } from "antd";
import { THEME_COLOR } from "constants/style.constants";
import styled from "styled-components";

import ColorPicker from "components/base/ColorPicker";
import ColorPickerWarningMessage from "components/base/ColorPickerWarningMessage";
import ColorPaletteList from "components/filter/focus/ColorPaletteList";
import {
  ActionListWrapper,
  ShapefileAction
} from "components/project/layers/components/layer-managers/shared/nodes/StyledNodePartials";
import { IColorPalette } from "components/user-settings/models";

interface IRigFracColorActionsProps {
  isDisabled?: boolean;
  onApplyPalette: (palette: IColorPalette) => void;
  onColorAll: (color: string) => void;
}

/**
 * "Colour Actions" popover that sits beside the Color By dropdown, mirroring the
 * shapefile attribute action button. The icon opens a popover with two rows:
 * "Apply Colour Palette" (colours the whole domain from a palette) and
 * "Colour All" (paints one chosen colour across every value in the domain).
 */
export function RigFracColorActions({
  isDisabled,
  onApplyPalette,
  onColorAll
}: IRigFracColorActionsProps): JSX.Element {
  const [actionsOpen, setActionsOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [colorAllOpen, setColorAllOpen] = useState(false);

  const closeAll = () => {
    setPaletteOpen(false);
    setColorAllOpen(false);
    setActionsOpen(false);
  };

  const actionList = (
    <ActionListWrapper>
      <Popover
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        overlayClassName="color-palette"
        placement="rightTop"
        trigger="click"
        content={
          <ColorPaletteListWrapper data-testid="rig-frac-palette-list">
            <ColorPaletteList
              selected={null}
              onSelect={(palette: IColorPalette) => {
                onApplyPalette(palette);
                closeAll();
              }}
              includeGeneratedPalettes
            />
          </ColorPaletteListWrapper>
        }>
        <ShapefileAction data-testid="rig-frac-apply-palette">
          <Icon path={mdiPalette} size={"18px"} />
          Apply Colour Palette
        </ShapefileAction>
      </Popover>

      {/* The picker is a controlled, anchorless sibling of the row (not a
          trigger="click" popover wrapping the row). Nesting a click-triggered
          popover inside the actions popover detaches the colour swatch while the
          inner ColorPicker measures it, dropping its chooser to the top-left
          corner. This mirrors the shapefile "Colour All" structure. */}
      <Popover
        open={colorAllOpen}
        onOpenChange={setColorAllOpen}
        overlayClassName="color-palette"
        placement="rightTop"
        content={
          <ColorAllWrapper data-testid="rig-frac-color-all-picker">
            <ColorPickerWarningMessage message="This will apply one colour to all values" />
            <ColorPicker
              isAtAttributeLevel={true}
              hasOutline={false}
              includeOpacity={false}
              values={{
                color: THEME_COLOR,
                strokeColor: THEME_COLOR,
                thickness: 2,
                opacity: 100
              }}
              onChange={(_prop, color) => {
                onColorAll(color as string);
                closeAll();
              }}
            />
          </ColorAllWrapper>
        }
      />
      <ShapefileAction
        data-testid="rig-frac-color-all"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setColorAllOpen(true);
        }}>
        <Icon path={mdiFormatColorFill} size={"18px"} />
        Colour All
      </ShapefileAction>
    </ActionListWrapper>
  );

  return (
    <Tooltip title="Colour Actions">
      <Popover
        arrowPointAtCenter={true}
        content={actionList}
        onOpenChange={(open) => {
          if (!isDisabled) {
            setActionsOpen(open);
          }
        }}
        open={actionsOpen}
        overlayClassName="popover-no-padding"
        placement="bottomLeft"
        trigger="click">
        <Button
          disabled={isDisabled}
          type="text"
          aria-label="Colour Actions"
          data-testid="rig-frac-color-actions-trigger">
          <Icon path={mdiFormatPaint} size={1.3} />
        </Button>
      </Popover>
    </Tooltip>
  );
}

const ColorPaletteListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ColorAllWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
